import { Box, Position, Rhythm, Text, TextSmall } from '@trmediaab/zebra';
import { useEffect, useRef, useState } from 'react';

import ErrorMessage from '../../../components/ErrorMessage';
import SwedbankPayClient from '../../../utils/swedbankpay';
import { useDomSafeId } from '../../../utils/useDomSafeId';
import InfoText from '../InfoText';
import type { PaymentFormProps } from '../types';

const SwedbankPayForm = ({
  order,
  site,
  user,
  onComplete,
  onAuthentication,
}: PaymentFormProps) => {
  const [error, setError] = useState();
  const id = useDomSafeId();

  const containerElementId = `paymentmenu-${id}`;
  const scriptElementId = `paymentmenu-script-${id}`;
  // React runs effects twice in development mode,
  // this ref is used to prevent multiple client instances
  const clientRef = useRef<SwedbankPayClient | null>(null);

  useEffect(() => {
    if (clientRef.current != null) {
      return;
    }
    const client = new SwedbankPayClient({
      site,
      orderId: order.id,
      containerElementId,
      scriptElementId,
      onCompleted: ({ res }) => {
        // New users gets a temporary login session when the order is completed
        if (res.headers.get('x-temporary-authentication')) {
          onAuthentication?.();
        }
        onComplete();
      },
      onError: error => {
        setError(error.message);
      },
    });

    clientRef.current = client;
    client.init();

    return () => {
      if (clientRef.current != null) {
        clientRef.current.destroy();
        clientRef.current = null;
      }
    };
  }, [
    containerElementId,
    onAuthentication,
    onComplete,
    order.id,
    scriptElementId,
    site,
  ]);

  return (
    <Rhythm between="6">
      <InfoText
        user={user}
        text="Vi använder Swedbank Pay för säkra betalningar. Vänligen slutför ditt köp genom att ange nedanstående uppgifter."
      />

      {error != null && <ErrorMessage error={error} />}

      {process.env.NODE_ENV === 'development' && (
        <Position.absolute top="1" left="2" data-no-between="true">
          {/* https://developer.swedbankpay.com/checkout-v3/resources/test-data */}
          <TextSmall>
            <a
              href="https://developer.swedbankpay.com/resources/test-data"
              target="blank"
              rel="noopener"
            >
              https://developer.swedbankpay.com/resources/test-data
            </a>
            <br />
            <Text.strong>Visa:</Text.strong> 4925 0000 0000 0004
            <br />
            <Text.strong>Visa 3-D secure:</Text.strong> 4761 7390 0101 0416
          </TextSmall>
        </Position.absolute>
      )}

      <Box
        sx={{
          iframe: {
            // https://stackoverflow.com/questions/69591128/chrome-is-forcing-a-white-background-on-frames-only-on-some-websites
            colorScheme: 'normal',
          },
        }}
        id={containerElementId}
      />
    </Rhythm>
  );
};

export default SwedbankPayForm;
